import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const BACKEND_COLORS = {
  local: '#10b981',
  runpod: '#6366f1',
  openai: '#f59e0b',
  anthropic: '#ec4899',
  cohere: '#8b5cf6'
};

const Budget = () => {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    fetchBudgetData();
  }, []);

  const fetchBudgetData = async () => {
    try {
      const [breakdownRes, configRes, logsRes] = await Promise.all([
        axios.get('/api/budget/breakdown'),
        axios.get('/api/budget/config'),
        axios.get('/api/inference/logs?limit=10')
      ]);
      setData(breakdownRes.data);
      setConfig(configRes.data.config);
      setLogs(logsRes.data.logs);
    } catch (error) {
      console.error('Failed to load budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (updates) => {
    try {
      await axios.put('/api/budget/config', updates);
      toast.success('Budget config updated');
      fetchBudgetData();
    } catch (error) {
      toast.error('Failed to update config');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const daily = data?.summary?.daily || {};
  const weekly = data?.summary?.weekly || {};
  const monthly = data?.summary?.monthly || {};
  const savings = data?.savings || {};

  // Prepare pie chart data
  const pieData = Object.entries(monthly.byBackend || {}).map(([key, val]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: val.requests || 0,
    cost: val.cost || 0
  })).filter(d => d.value > 0);

  // Prepare timeline data
  const timelineMap = {};
  for (const item of (data?.timeline || [])) {
    const date = item._id.date;
    if (!timelineMap[date]) timelineMap[date] = { date };
    timelineMap[date][item._id.backend] = item.totalCost;
  }
  const timelineData = Object.values(timelineMap).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Budget & Costs</h1>
        <button
          onClick={() => setShowConfig(!showConfig)}
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors"
        >
          {showConfig ? 'Hide Config' : 'Configure'}
        </button>
      </div>

      {/* Savings Banner */}
      {savings.amount > 0 && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center text-2xl">
              💰
            </div>
            <div>
              <p className="text-emerald-400 text-sm font-medium">Monthly Savings from Local Inference</p>
              <p className="text-3xl font-bold text-emerald-300">${savings.amount.toFixed(2)}</p>
              <p className="text-sm text-gray-400">
                {savings.localPercentage}% of requests handled locally ({savings.localRequests} of {savings.totalRequests})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SummaryCard
          title="Today"
          cost={daily.totalCost || 0}
          requests={daily.totalRequests || 0}
          limit={config?.limits?.daily || 5}
          color="from-blue-500 to-cyan-500"
        />
        <SummaryCard
          title="This Week"
          cost={weekly.totalCost || 0}
          requests={weekly.totalRequests || 0}
          limit={config?.limits?.weekly || 25}
          color="from-violet-500 to-purple-500"
        />
        <SummaryCard
          title="This Month"
          cost={monthly.totalCost || 0}
          requests={monthly.totalRequests || 0}
          limit={config?.limits?.monthly || 80}
          color="from-amber-500 to-orange-500"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Requests by Backend */}
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <h3 className="text-lg font-semibold mb-4">Requests by Backend</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={BACKEND_COLORS[entry.name.toLowerCase()] || '#6b7280'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e1e2e', border: '1px solid #2d2d3d', borderRadius: '8px' }}
                  formatter={(value, name, props) => [`${value} requests ($${props.payload.cost.toFixed(4)})`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-16">No data yet</p>
          )}
        </div>

        {/* Line Chart - Spending Over Time */}
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <h3 className="text-lg font-semibold mb-4">Spending Over Time</h3>
          {timelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2d2d3d" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} />
                <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} tickFormatter={v => `$${v.toFixed(2)}`} />
                <Tooltip
                  contentStyle={{ background: '#1e1e2e', border: '1px solid #2d2d3d', borderRadius: '8px' }}
                  formatter={(v) => [`$${(v || 0).toFixed(4)}`, '']}
                />
                <Line type="monotone" dataKey="local" stroke="#10b981" strokeWidth={2} dot={false} name="Local" />
                <Line type="monotone" dataKey="runpod" stroke="#6366f1" strokeWidth={2} dot={false} name="RunPod" />
                <Line type="monotone" dataKey="openai" stroke="#f59e0b" strokeWidth={2} dot={false} name="OpenAI" />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-16">No data yet</p>
          )}
        </div>
      </div>

      {/* Recent Inference Logs */}
      <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Inference Requests</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-[#2d2d3d]">
                <th className="text-left py-2 px-3">Time</th>
                <th className="text-left py-2 px-3">Backend</th>
                <th className="text-left py-2 px-3">Model</th>
                <th className="text-left py-2 px-3">Tokens</th>
                <th className="text-left py-2 px-3">Cost</th>
                <th className="text-left py-2 px-3">Time (ms)</th>
                <th className="text-left py-2 px-3">Routing Reason</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No inference logs yet</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className="border-b border-[#2d2d3d]/50 hover:bg-[#1e1e2e]">
                    <td className="py-2 px-3 text-gray-400">
                      {new Date(log.createdAt).toLocaleTimeString()}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getBackendBadge(log.backend)}`}>
                        {log.backend}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-300">{log.model}</td>
                    <td className="py-2 px-3">{log.tokens?.total || 0}</td>
                    <td className="py-2 px-3 text-emerald-400">${(log.cost || 0).toFixed(4)}</td>
                    <td className="py-2 px-3">{log.processingTime || 0}</td>
                    <td className="py-2 px-3 text-gray-400 text-xs max-w-[200px] truncate">{log.routingReason}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
          <h3 className="text-lg font-semibold mb-4">Budget Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-300">Spending Limits</h4>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Daily Limit ($)</label>
                <input
                  type="number"
                  step="0.50"
                  defaultValue={config?.limits?.daily || 5}
                  onBlur={(e) => updateConfig({ limits: { daily: parseFloat(e.target.value) } })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Weekly Limit ($)</label>
                <input
                  type="number"
                  step="1"
                  defaultValue={config?.limits?.weekly || 25}
                  onBlur={(e) => updateConfig({ limits: { weekly: parseFloat(e.target.value) } })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Monthly Limit ($)</label>
                <input
                  type="number"
                  step="5"
                  defaultValue={config?.limits?.monthly || 80}
                  onBlur={(e) => updateConfig({ limits: { monthly: parseFloat(e.target.value) } })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-300">Routing Preferences</h4>
              <ToggleOption
                label="Prefer Local Inference"
                description="Route simple tasks to free local Ollama"
                checked={config?.routing?.preferLocal ?? true}
                onChange={(val) => updateConfig({ routing: { preferLocal: val } })}
              />
              <ToggleOption
                label="Auto Fallback"
                description="Fallback to RunPod when local is unhealthy"
                checked={config?.routing?.autoFallback ?? true}
                onChange={(val) => updateConfig({ routing: { autoFallback: val } })}
              />
              <ToggleOption
                label="Force Local on Budget Exceeded"
                description="Only use free local when daily budget is exceeded"
                checked={config?.routing?.forceLocalOnBudgetExceeded ?? true}
                onChange={(val) => updateConfig({ routing: { forceLocalOnBudgetExceeded: val } })}
              />
              <div>
                <label className="block text-sm text-gray-400 mb-1">Local Threshold (tokens)</label>
                <input
                  type="number"
                  step="500"
                  defaultValue={config?.routing?.localThresholdTokens || 4000}
                  onBlur={(e) => updateConfig({ routing: { localThresholdTokens: parseInt(e.target.value) } })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Requests under this token count go to local model</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ title, cost, requests, limit, color }) => {
  const percentage = limit > 0 ? Math.min((cost / limit) * 100, 100) : 0;

  return (
    <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-gray-400 text-sm">{title}</p>
        <span className="text-xs text-gray-500">{requests} requests</span>
      </div>
      <p className="text-2xl font-bold mb-2">${cost.toFixed(2)}</p>
      <div className="h-2 bg-[#2d2d3d] rounded-full overflow-hidden mb-1">
        <div
          className={`h-full bg-gradient-to-r ${color} transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-gray-500">${cost.toFixed(2)} of ${limit.toFixed(2)} limit ({percentage.toFixed(0)}%)</p>
    </div>
  );
};

const ToggleOption = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="font-medium text-sm">{label}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`w-12 h-6 rounded-full transition-colors ${checked ? 'bg-indigo-500' : 'bg-gray-600'}`}
    >
      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

const getBackendBadge = (backend) => {
  const badges = {
    local: 'bg-emerald-500/20 text-emerald-400',
    runpod: 'bg-indigo-500/20 text-indigo-400',
    openai: 'bg-amber-500/20 text-amber-400',
    anthropic: 'bg-pink-500/20 text-pink-400',
    cohere: 'bg-purple-500/20 text-purple-400'
  };
  return badges[backend] || 'bg-gray-500/20 text-gray-400';
};

export default Budget;
